import { useCallback, useMemo, useState } from 'react'

import { useDebounce } from 'shared/hooks/useDebounce'
import type { LogEntry } from 'shared/services/logger/logger.types'

import type {
  ConditionField,
  FilterCondition,
  FilterLogic,
  LevelFilter,
  SortOrder,
  TypeFilter
} from '../logViewer.types'
import { NO_VALUE_OPS, OPERATORS_FOR_FIELD } from '../logViewer.types'
import { evaluateCondition, toDatetimeLocalValue } from '../logViewer.utils'

const DEBOUNCE_MS = 300

export function useLogFilter(activeEntries: LogEntry[]) {
  const [levelFilter, setLevelFilter] = useState<LevelFilter>('all')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [onlyWithPayload, setOnlyWithPayload] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [conditions, setConditions] = useState<FilterCondition[]>([])
  const [filterLogic, setFilterLogic] = useState<FilterLogic>('and')

  // Debounce date inputs — condition text inputs are debounced at source (ConditionRow)
  const debouncedDateFrom = useDebounce(dateFrom, DEBOUNCE_MS)
  const debouncedDateTo = useDebounce(dateTo, DEBOUNCE_MS)

  const activeConditions = useMemo(
    () =>
      conditions.filter(
        c =>
          NO_VALUE_OPS.has(c.operator) ||
          c.value.trim() !== '' ||
          (c.field === 'payload_field' && c.payloadKey.trim() !== '')
      ),
    [conditions]
  )

  const isAnyFilterActive =
    levelFilter !== 'all' ||
    typeFilter !== 'all' ||
    debouncedDateFrom !== '' ||
    debouncedDateTo !== '' ||
    onlyWithPayload ||
    activeConditions.length > 0

  const filteredEntries = useMemo(() => {
    const fromTs = debouncedDateFrom
      ? new Date(debouncedDateFrom).getTime()
      : null
    const toTs = debouncedDateTo ? new Date(debouncedDateTo).getTime() : null

    const result = activeEntries.filter(e => {
      if (levelFilter !== 'all' && e.level !== levelFilter) return false
      if (typeFilter !== 'all' && e.type !== typeFilter) return false
      if (onlyWithPayload && Object.keys(e.payload).length === 0) return false
      if (fromTs != null && e.timestamp < fromTs) return false
      if (toTs != null && e.timestamp > toTs) return false

      if (activeConditions.length > 0) {
        const results = activeConditions.map(c => evaluateCondition(e, c))
        return filterLogic === 'and'
          ? results.every(Boolean)
          : results.some(Boolean)
      }

      return true
    })

    result.sort((a, b) =>
      sortOrder === 'desc'
        ? b.timestamp - a.timestamp
        : a.timestamp - b.timestamp
    )
    return result
  }, [
    activeEntries,
    levelFilter,
    typeFilter,
    sortOrder,
    debouncedDateFrom,
    debouncedDateTo,
    onlyWithPayload,
    activeConditions,
    filterLogic
  ])

  const levelCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    activeEntries.forEach(e => {
      counts[e.level] = (counts[e.level] ?? 0) + 1
    })
    return counts
  }, [activeEntries])

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    activeEntries.forEach(e => {
      counts[e.type] = (counts[e.type] ?? 0) + 1
    })
    return counts
  }, [activeEntries])

  const addCondition = useCallback(
    () =>
      setConditions(cs => [
        ...cs,
        {
          id: crypto.randomUUID(),
          field: 'event' as ConditionField,
          payloadKey: '',
          operator: 'contains',
          value: ''
        }
      ]),
    []
  )

  const removeCondition = useCallback(
    (id: string) => setConditions(cs => cs.filter(c => c.id !== id)),
    []
  )

  const updateCondition = useCallback(
    (id: string, patch: Partial<FilterCondition>) =>
      setConditions(cs =>
        cs.map(c => {
          if (c.id !== id) return c
          const updated = { ...c, ...patch }
          if (patch.field && patch.field !== c.field) {
            const validOps = OPERATORS_FOR_FIELD[patch.field]
            if (!validOps.includes(updated.operator))
              updated.operator = validOps[0]
          }
          return updated
        })
      ),
    []
  )

  const clearAll = useCallback(() => {
    setLevelFilter('all')
    setTypeFilter('all')
    setDateFrom('')
    setDateTo('')
    setOnlyWithPayload(false)
    setConditions([])
  }, [])

  const toggleSortOrder = useCallback(
    () => setSortOrder(o => (o === 'desc' ? 'asc' : 'desc')),
    []
  )
  const toggleShowAdvanced = useCallback(() => setShowAdvanced(v => !v), [])
  const toggleOnlyWithPayload = useCallback(
    () => setOnlyWithPayload(v => !v),
    []
  )

  const setDateRangeFromEntries = useCallback(() => {
    if (!activeEntries.length) return
    const ts = activeEntries.map(e => e.timestamp)
    setDateFrom(toDatetimeLocalValue(Math.min(...ts)))
    setDateTo(toDatetimeLocalValue(Math.max(...ts)))
  }, [activeEntries])

  return {
    levelFilter,
    setLevelFilter,
    typeFilter,
    setTypeFilter,
    sortOrder,
    toggleSortOrder,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    onlyWithPayload,
    toggleOnlyWithPayload,
    showAdvanced,
    toggleShowAdvanced,
    conditions,
    filterLogic,
    setFilterLogic,
    activeConditions,
    isAnyFilterActive,
    filteredEntries,
    levelCounts,
    typeCounts,
    addCondition,
    removeCondition,
    updateCondition,
    clearAll,
    setDateRangeFromEntries
  }
}

export type LogFilterState = ReturnType<typeof useLogFilter>
